import { useMemo } from 'react';

import {
  type CustomRoleTaxonomySelection,
  TaxonomyType,
} from '@revelio/data-access';

import {
  type RoleSelectionListKey,
  roleSelectionListKeys,
  type SelectionListState,
} from '../types';
import { getRoleTaxonomyId } from '../utils/roleTaxonomy';
import { treeToRoleSelectionLists } from '../utils/treeToRoleSelectionLists';
import { useTaxonomyTree } from './useTaxonomyTree';

type RoleSelectionListState = SelectionListState<RoleSelectionListKey>;

// Role keys map 1:1 to role tree levels by position: role_k7 → level 1 …
// role_k15000 → level 7. The tree tags the ends role_k10 / role_k10000; that
// tag→key aliasing is handled in treeToRoleSelectionLists, so any combination
// of keys (including the ends) resolves correctly.
const roleKeyTreeLevel = (key: RoleSelectionListKey): number =>
  roleSelectionListKeys.indexOf(key) + 1;

/**
 * Sources role selection lists from the role TaxonomyTree (the tree-backed
 * replacement for the legacy RoleSelectionList query). Returns parent-linked
 * lists keyed by the requested role keys, or null while the tree is loading.
 */
export const useRoleTreeSelectionLists = ({
  requiredKeys,
  taxonomySelection,
  pause = false,
}: {
  requiredKeys: RoleSelectionListKey[];
  taxonomySelection?: CustomRoleTaxonomySelection;
  pause?: boolean;
}): RoleSelectionListState | null => {
  // Stabilise on the sorted key string so an inline-array caller doesn't
  // recompute levels / rebuild the result on every render (mirrors
  // useSelectionListsForKeys).
  const keysKey = [...requiredKeys].sort().join(',');
  // biome-ignore lint/correctness/useExhaustiveDependencies: keyed on the sorted key string above
  const stableKeys = useMemo(() => requiredKeys, [keysKey]);

  const levels = useMemo(
    () => stableKeys.map(roleKeyTreeLevel).sort((a, b) => a - b),
    [stableKeys]
  );

  const {
    treeData,
    levels: levelMetadata,
    fetching,
  } = useTaxonomyTree({
    taxonomyType: TaxonomyType.Role,
    taxonomyId: getRoleTaxonomyId(taxonomySelection),
    levels,
    pause: pause || stableKeys.length === 0,
  });

  return useMemo(() => {
    if (fetching || treeData.length === 0) return null;

    const lists = treeToRoleSelectionLists(treeData, levelMetadata);
    const result: Partial<Record<RoleSelectionListKey, unknown[]>> = {};
    for (const key of stableKeys) {
      if (lists[key]) result[key] = lists[key];
    }

    return result as RoleSelectionListState;
  }, [fetching, treeData, levelMetadata, stableKeys]);
};
